import {
  getFontCss,
  HoverableLinkSizes,
  LockModal,
  LockTypes,
  ProcessingModal,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import queryString from 'query-string'
import React from 'react'
import { useSelector } from 'react-redux'
import ResourceCenterLink from 'src/components/ResourceCenterLink/ResourceCenterLink'
import { useFlyoutDispatch } from 'src/hooks'
import Trns from 'src/localization/Trns'
import { ScreeningStatus } from 'src/models/virtualCard.models'
import { closeModal } from 'src/navigation/Lnk'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectLocationFromState } from 'src/redux/selectors/router.selectors'
import styled, { css } from 'styled-components'

const UserScreeningInfo: React.FC = () => {
  const user = useSelector(selectUserListModelFromState)
  const location = useSelector(selectLocationFromState)

  const params = queryString.parse(location.search)
  const dispatch = useFlyoutDispatch()

  if (user === undefined || user.issuerSanctions === undefined) {
    return null
  }

  const BoldSpan = styled.span`
    ${getFontCss(
      16,
      themeConstants.colors.font_primary,
      styleConstants.fontFamilies.medium,
    )};
  `

  const GetBankNames = (
    banks: string[] | React.ReactElement[],
  ): React.ReactElement => {
    const banksLength = banks.length

    const comma = <span>{', '}</span>

    const and = (
      <span>
        {' '}
        <Trns source={'and'} />{' '}
      </span>
    )
    const returnVal = banks.flatMap((n, i) =>
      i + 1 === banksLength - 1
        ? [n, and]
        : i + 1 < banksLength
          ? [n, comma]
          : [n],
    )

    return <>{returnVal.map((item) => item)}</>
  }

  const forbiddenSanctions = user.issuerSanctions
    .filter((sanction) => sanction.status === ScreeningStatus.Forbidden)
    .map((sanction) => sanction.name)
  const failSanctions = user.issuerSanctions
    .filter((sanction) => sanction.status === ScreeningStatus.Fail)
    .map((sanction) => sanction.name)

  switch (params.status) {
    case ScreeningStatus.Forbidden: {
      return (
        <LockModal
          onClickClose={(): void => {
            dispatch(closeModal())
          }}
          lockType={LockTypes.Hard}
          title={<Trns source={'You have been locked'} />}
          message={
            <Trns
              source={
                'You cannot use ${bold}. If you believe this lock is in error, please contact the person who sent you the virtual card for resolution.'
              }
              expressions={{
                bold: (
                  <BoldSpan>
                    <Trns
                      source={'${banks} virtual cards'}
                      expressions={{
                        banks: GetBankNames(forbiddenSanctions),
                      }}
                    />
                  </BoldSpan>
                ),
              }}
            />
          }
        />
      )
    }
    case ScreeningStatus.Fail: {
      return (
        <LockModal
          onClickClose={(): void => {
            dispatch(closeModal())
          }}
          lockType={LockTypes.Soft}
          title={<Trns source={'Your account is under review'} />}
          message={
            <div>
              <p>
                <Trns
                  source={
                    "We're having trouble confirming your identity based on the information we have. Until your identity is confirmed, you will not have access to any ${bold} associated with your account."
                  }
                  expressions={{
                    bold: (
                      <BoldSpan>
                        <Trns
                          source={'${banks} virtual cards'}
                          expressions={{
                            banks: GetBankNames(failSanctions),
                          }}
                        />
                      </BoldSpan>
                    ),
                  }}
                />
              </p>
              <p
                css={css`
                  margin-top: 20px;
                `}
              >
                <Trns
                  source={
                    'For resolution, please contact the person who sent you the virtual card, or visit our ${resourceCenter} for support and contact options. You may be required to provide a government-issued ID to confirm your identity.'
                  }
                  expressions={{
                    resourceCenter: (
                      <ResourceCenterLink
                        size={HoverableLinkSizes.Medium}
                        underline
                      />
                    ),
                  }}
                />
              </p>
            </div>
          }
        />
      )
    }
    case ScreeningStatus.ServiceUnavailable: {
      return (
        <ProcessingModal
          onClickClose={(): void => {
            dispatch(closeModal())
          }}
          title={<Trns source={'Your information is being processed'} />}
          message={
            <>
              <p
                css={css`
                  ${getFontCss(
                    16,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.regular,
                  )};
                  margin-bottom: 30px;
                `}
              >
                <Trns
                  source={
                    '${theresNothingElse} This may take a few minutes, so check back in a few minutes for any updates.'
                  }
                  expressions={{
                    theresNothingElse: (
                      <BoldSpan>
                        <Trns source={"There's nothing else you need to do."} />
                      </BoldSpan>
                    ),
                  }}
                />
              </p>
              <p
                css={css`
                  ${getFontCss(
                    16,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.regular,
                  )};
                `}
              >
                <Trns
                  source={
                    "Have questions? We're here to help. Visit our ${resourceCenter} for support and contact options."
                  }
                  expressions={{
                    resourceCenter: (
                      <ResourceCenterLink
                        size={HoverableLinkSizes.Medium}
                        underline
                      />
                    ),
                  }}
                />
              </p>
            </>
          }
        />
      )
    }
    default: {
      return null
    }
  }
}

export default UserScreeningInfo
